import React from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLocalizedNav } from '../i18n/useLocalizedNav';
import { pick, formatNewsDate } from '../i18n/pick';
import { useDocumentMeta } from '../i18n/useDocumentMeta';
import { newsData } from '../data/newsData';

const NewsDetailPage = () => {
  const { id } = useParams();
  const { t } = useTranslation();
  const { lang, navigate } = useLocalizedNav();
  const newsId = parseInt(id);
  const news = newsData.find(n => n.id === newsId);

  // 記事が無い場合はタイトルだけ差し替え、説明文は記事本文の冒頭を使う
  const articleTitle = news ? pick(news.title, lang) : t('news.notFound');
  useDocumentMeta('newsDetail', {
    titleValues: { title: articleTitle },
    description: news ? pick(news.content, lang).trim().slice(0, 120) : undefined,
  });

  const styles = {
    subPage: {
      minHeight: 'calc(100vh - 70px)',
      paddingBottom: '60px',
    },
    subPageHeader: {
      backgroundColor: '#2563eb',
      color: '#ffffff',
      padding: '80px 20px',
      textAlign: 'center',
    },
    subPageTitle: {
      fontSize: '48px',
      fontWeight: 'bold',
      marginBottom: '10px',
    },
    subPageSubtitle: {
      fontSize: '18px',
      color: '#dbeafe',
    },
    container: {
      maxWidth: '900px',
      margin: '0 auto',
      padding: '60px 20px',
    },
    backButtonContainer: {
      marginBottom: '30px',
    },
    backButton: {
      background: 'none',
      border: '2px solid #2563eb',
      color: '#2563eb',
      padding: '10px 24px',
      fontSize: '14px',
      borderRadius: '6px',
      cursor: 'pointer',
      fontWeight: 'bold',
      transition: 'all 0.3s',
    },
    newsHeader: {
      backgroundColor: '#ffffff',
      padding: '40px',
      borderRadius: '8px',
      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
      marginBottom: '30px',
    },
    newsMetaContainer: {
      display: 'flex',
      alignItems: 'center',
      gap: '15px',
      marginBottom: '20px',
    },
    newsDate: {
      color: '#6b7280',
      fontSize: '16px',
      fontWeight: 'bold',
    },
    newsCategory: {
      backgroundColor: '#dbeafe',
      color: '#2563eb',
      padding: '6px 16px',
      borderRadius: '4px',
      fontSize: '14px',
      fontWeight: 'bold',
    },
    newsTitle: {
      fontSize: '28px',
      fontWeight: 'bold',
      color: '#1f2937',
      lineHeight: '1.5',
    },
    newsContent: {
      backgroundColor: '#ffffff',
      padding: '40px',
      borderRadius: '8px',
      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
      marginBottom: '30px',
    },
    newsImage: {
      width: '100%',
      height: 'auto',
      borderRadius: '8px',
      marginBottom: '30px',
    },
    contentText: {
      fontSize: '16px',
      lineHeight: '1.9',
      color: '#4b5563',
      whiteSpace: 'pre-line',
      marginBottom: '30px',
      textAlign: 'left',
    },
    linksContainer: {
      marginTop: '30px',
      paddingTop: '30px',
      borderTop: '1px solid #e5e7eb',
    },
    linksTitle: {
      fontSize: '18px',
      fontWeight: 'bold',
      marginBottom: '15px',
      color: '#1f2937',
    },
    linkItem: {
      marginBottom: '15px',
    },
    linkButton: {
      display: 'inline-block',
      backgroundColor: '#2563eb',
      color: '#ffffff',
      padding: '12px 24px',
      borderRadius: '6px',
      textDecoration: 'none',
      fontSize: '15px',
      fontWeight: 'bold',
      transition: 'background-color 0.3s',
    },
    backToListContainer: {
      textAlign: 'center',
    },
    backToListButton: {
      backgroundColor: '#2563eb',
      color: '#ffffff',
      border: 'none',
      padding: '12px 30px',
      fontSize: '16px',
      borderRadius: '8px',
      cursor: 'pointer',
      fontWeight: 'bold',
      transition: 'all 0.3s',
    },
    errorMessage: {
      textAlign: 'center',
      padding: '60px 20px',
    },
  };

  if (!news) {
    return (
      <div style={styles.subPage}>
        <style>
          {`
            @media (max-width: 768px) {
              .sub-page-header-detail-mobile {
                padding: 60px 20px !important;
              }
              .sub-page-title-detail-mobile {
                font-size: 36px !important;
              }
              .sub-page-subtitle-detail-mobile {
                font-size: 16px !important;
              }
            }
          `}
        </style>
        <div style={styles.subPageHeader} className="sub-page-header-detail-mobile">
          <h1 style={styles.subPageTitle} className="sub-page-title-detail-mobile">NEWS</h1>
          <p style={styles.subPageSubtitle} className="sub-page-subtitle-detail-mobile">{t('news.subtitle')}</p>
        </div>
        <div style={styles.container}>
          <div style={styles.errorMessage}>
            <p>{t('news.notFound')}</p>
            <button onClick={() => navigate('/news')} style={styles.backButton}>
              {t('news.backToList')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.subPage}>
      <style>
        {`
          @media (max-width: 768px) {
            .sub-page-header-detail-mobile {
              padding: 60px 20px !important;
            }
            .sub-page-title-detail-mobile {
              font-size: 36px !important;
            }
            .sub-page-subtitle-detail-mobile {
              font-size: 16px !important;
            }
            .container-detail-mobile {
              padding: 40px 15px !important;
            }
            .news-header-mobile {
              padding: 25px !important;
            }
            .news-meta-container-mobile {
              flex-wrap: wrap !important;
              gap: 10px !important;
            }
            .news-date-mobile {
              font-size: 14px !important;
            }
            .news-category-mobile {
              font-size: 12px !important;
              padding: 4px 12px !important;
            }
            .news-title-mobile {
              font-size: 22px !important;
            }
            .news-content-mobile {
              padding: 25px !important;
            }
            .news-image-mobile {
              margin-bottom: 20px !important;
            }
            .content-text-mobile {
              font-size: 15px !important;
              line-height: 1.8 !important;
              margin-bottom: 20px !important;
            }
            .links-title-mobile {
              font-size: 16px !important;
            }
            .link-button-mobile {
              font-size: 14px !important;
              padding: 10px 20px !important;
              display: block !important;
              text-align: center !important;
            }
            .back-button-mobile {
              font-size: 13px !important;
              padding: 8px 20px !important;
            }
            .back-to-list-button-mobile {
              font-size: 15px !important;
              padding: 10px 25px !important;
            }
          }
        `}
      </style>
      <div style={styles.subPageHeader} className="sub-page-header-detail-mobile">
        <h1 style={styles.subPageTitle} className="sub-page-title-detail-mobile">NEWS</h1>
        <p style={styles.subPageSubtitle} className="sub-page-subtitle-detail-mobile">{t('news.subtitle')}</p>
      </div>
      
      <div style={styles.container} className="container-detail-mobile">
        <div style={styles.backButtonContainer}>
          <button onClick={() => navigate('/news')} style={styles.backButton} className="back-button-mobile">
            ← {t('news.backToList')}
          </button>
        </div>

        <div style={styles.newsHeader} className="news-header-mobile">
          <div style={styles.newsMetaContainer} className="news-meta-container-mobile">
            <span style={styles.newsDate} className="news-date-mobile">{formatNewsDate(news.date, lang)}</span>
            <span style={styles.newsCategory} className="news-category-mobile">{pick(news.category, lang)}</span>
          </div>
          <h2 style={styles.newsTitle} className="news-title-mobile">{pick(news.title, lang)}</h2>
        </div>

        <div style={styles.newsContent} className="news-content-mobile">
          {news.image && (
            <img 
              src={news.image} 
              alt={pick(news.title, lang)} 
              style={styles.newsImage}
              className="news-image-mobile"
            />
          )}
          <p style={styles.contentText} className="content-text-mobile content-text-left">{pick(news.content, lang)}</p>
          
          {news.links && news.links.length > 0 && (
            <div style={styles.linksContainer}>
              <p style={styles.linksTitle} className="links-title-mobile">
                {t('news.linksTitle')}
              </p>
              {news.links.map((link, index) => (
                <div key={index} style={styles.linkItem}>
                  <a 
                    href={link.url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    style={styles.linkButton}
                    className="link-button-mobile"
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#1e40af'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#2563eb'}
                  >
                    {pick(link.text, lang)} →
                  </a>
                </div>
              ))}
            </div>
          )}
        </div>

        <div style={styles.backToListContainer}>
          <button onClick={() => navigate('/news')} style={styles.backToListButton} className="back-to-list-button-mobile">
            {t('news.backToList')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default NewsDetailPage;